import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    InputAdornment,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import { useSearchParams } from 'react-router-dom';
import { MetaTags } from '../../components/Metatags/Metatags';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { BRAND_SECONDARY } from './brandDashboardTokens';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getWalletDashboard, getWalletFundOptions, updateAutoReload } from '../../redux/buzzbridge/actions';
import {
    gettingWalletDashboardSelector,
    gettingWalletFundOptionsSelector,
    walletDashboardSelector,
} from '../../redux/buzzbridge/selectors';
import { IWalletTransaction } from '../../redux/buzzbridge/types';
import WalletPaymentSection from './WalletPaymentSection';
import { WalletPaystackFunding } from './WalletPaystackFunding';
import { useWalletFundOptions } from '../../hook/useWalletFundOptions';
import type { WalletFundProvider } from '../../redux/buzzbridge/types';
import {
    clearWalletFundReturnUrl,
    defaultWalletFundProvider,
    formatPaymentRailChannels,
    flutterwaveCheckoutAvailable,
    paystackCheckoutAvailable,
    readWalletFundReturnUrl,
    stripeCheckoutAvailable,
} from './walletFundRailUtils';
import { showToastMessage } from '../../utils/AppUtils';
import type { IWalletFundPreset } from '../../redux/buzzbridge/types';
import {
    chargesInPaymentCurrency,
    findMatchingPreset,
    formatAmountInputMajor,
    formatApiAmountLabel,
    formatYouPayDisplay,
    getAdminPresets,
    getAdminTestPresetDisplayTitle,
    getAllWalletFundPresets,
    getMinimumFundLabel,
    getPaymentCurrencySymbol,
    parseAmountInputMajor,
    presetToPaymentInputAmount,
    presetToUsdInputAmountFormatted,
} from './walletFundOptionsUtils';

const PAYMENT_METHODS = [
    { label: 'Credit / debit card', sub: 'Visa / Mastercard', Icon: CreditCardIcon, locked: false },
    { label: 'Bank wire', sub: 'SWIFT / ACH', Icon: AccountBalanceIcon, locked: true },
    { label: 'Crypto wallet', sub: 'ETH / USDC', Icon: CurrencyBitcoinIcon, locked: true },
];

function formatCents(cents: number): string {
    return (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function txLabel(tx: IWalletTransaction): string {
    const map: Record<string, string> = {
        deposit: 'Card deposit',
        auto_reload: 'Auto-reload',
        escrow_lock: 'Escrow lock',
        escrow_release: 'Escrow release',
        escrow_refund: 'Escrow refund',
        payout: 'Payout',
    };
    return tx.description || map[tx.type] || tx.type;
}

function txDate(iso: string): string {
    const d = new Date(iso);
    return (
        d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' \u2022 ' +
        d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    );
}

export default function BrandWalletPage() {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const fundSectionRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();

    const dashboard = useSelector(walletDashboardSelector);
    const loading = useSelector(gettingWalletDashboardSelector);
    const loadingFundOptions = useSelector(gettingWalletFundOptionsSelector);
    const { fundOptions } = useWalletFundOptions();

    const [method, setMethod] = useState(0);
    const [amountStr, setAmountStr] = useState('');
    const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
    const [autoReload, setAutoReload] = useState(true);
    const [fundingProvider, setFundingProvider] = useState<WalletFundProvider>('stripe');

    const topupPrompt = searchParams.get('topup') === '1';
    const topupReason = searchParams.get('reason') || '';

    const b = (color: string) => '1px solid ' + color;

    useEffect(() => {
        dispatch(getWalletDashboard());
    }, [dispatch]);

    useEffect(() => {
        if (!topupPrompt) return;
        const id = window.setTimeout(() => {
            fundSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 120);
        return () => window.clearTimeout(id);
    }, [topupPrompt]);

    useEffect(() => {
        if (fundOptions) setFundingProvider(defaultWalletFundProvider(fundOptions));
    }, [fundOptions]);

    useEffect(() => {
        // Sync auto-reload toggle from server
        // dashboard doesn't directly contain the toggle — we keep local for now
    }, [dashboard]);

    const handleAutoReloadToggle = useCallback(
        (_: React.SyntheticEvent, checked: boolean) => {
            setAutoReload(checked);
            dispatch(updateAutoReload({ enabled: checked }));
        },
        [dispatch],
    );

    const handlePaymentComplete = useCallback(() => {
        setAmountStr('');
        dispatch(getWalletDashboard());
        dispatch(getWalletFundOptions());
        setSelectedPresetId(null);
        const returnUrl = readWalletFundReturnUrl();
        if (returnUrl && returnUrl.startsWith('/')) {
            clearWalletFundReturnUrl();
            showToastMessage('Funds added. Returning to your campaign…', 'success');
            navigate(returnUrl, { replace: true });
        }
    }, [dispatch, navigate]);

    const availableFunds = dashboard?.available_funds_cents ?? 0;
    const escrow = dashboard?.locked_in_escrow_cents ?? 0;
    const spentThisMonth = dashboard?.spent_this_month_cents ?? 0;
    const pctChange = dashboard?.monthly_percentage_change ?? 0;
    const transactions = dashboard?.recent_transactions ?? [];
    const fundingActivity = dashboard?.funding_activity ?? [];

    const maxBar = Math.max(...fundingActivity.map(f => f.total_cents), 1);
    const chargesInLocal = chargesInPaymentCurrency(fundOptions);
    const adminPresets = useMemo(() => getAdminPresets(fundOptions), [fundOptions]);
    const standardPresets = useMemo(() => fundOptions?.presets ?? [], [fundOptions?.presets]);
    const allPresets = useMemo(() => getAllWalletFundPresets(fundOptions), [fundOptions]);

    const selectedPreset = useMemo<IWalletFundPreset | null>(
        () => allPresets.find(p => p.id === selectedPresetId) ?? null,
        [allPresets, selectedPresetId],
    );

    const customPaymentChargeCents = useMemo(() => {
        if (selectedPreset || !chargesInLocal) return null;
        return Math.round(parseAmountInputMajor(amountStr) * 100);
    }, [selectedPreset, chargesInLocal, amountStr]);

    const walletCreditUsdCents = useMemo(() => {
        if (selectedPreset) return selectedPreset.wallet_credit.amount_cents;
        if (!chargesInLocal) return Math.round(parseAmountInputMajor(amountStr) * 100);
        return 0;
    }, [selectedPreset, amountStr, chargesInLocal]);

    const activePreset = useMemo(
        () => selectedPreset ?? findMatchingPreset(fundOptions, walletCreditUsdCents) ?? null,
        [selectedPreset, fundOptions, walletCreditUsdCents],
    );

    const paySymbol = useMemo(
        () => getPaymentCurrencySymbol(fundOptions, activePreset ?? selectedPreset),
        [fundOptions, activePreset, selectedPreset],
    );

    const showPaystack = paystackCheckoutAvailable(fundOptions) && fundingProvider === 'paystack';
    const showFlutterwave = flutterwaveCheckoutAvailable(fundOptions) && fundingProvider === 'flutterwave';
    const showHostedCheckout = showPaystack || showFlutterwave;
    const showStripe = stripeCheckoutAvailable(fundOptions) && fundingProvider === 'stripe';
    const paymentChannels = formatPaymentRailChannels(fundOptions);

    const handlePresetSelect = useCallback(
        (preset: IWalletFundPreset) => {
            setSelectedPresetId(preset.id);
            setAmountStr(chargesInLocal ? presetToPaymentInputAmount(preset) : presetToUsdInputAmountFormatted(preset));
        },
        [chargesInLocal],
    );

    const handleAmountChange = useCallback((value: string) => {
        setAmountStr(formatAmountInputMajor(value));
        setSelectedPresetId(null);
    }, []);

    if (loading && !dashboard) {
        return (
            <MetaTags title="Wallet | Brand | Thspian" description="Campaign funds and wallet">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress sx={{ color: t.primary_orange }} />
                </Box>
            </MetaTags>
        );
    }

    return (
        <MetaTags title="Wallet | Brand | Thspian" description="Campaign funds and wallet">
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '7fr 5fr' },
                    gap: 3,
                    alignItems: 'start',
                }}>
                {/* ── Left column ─────────────────────────────── */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Balance card */}
                    <BalanceCard
                        t={t}
                        b={b}
                        availableFunds={availableFunds}
                        pctChange={pctChange}
                        escrow={escrow}
                        spentThisMonth={spentThisMonth}
                    />

                    {/* Add funds card */}
                    <Box
                        ref={fundSectionRef}
                        sx={{ borderRadius: 2, p: 3, bgcolor: t.deep_grey_lighter, border: b(t.selectableBorder) }}>
                        {topupPrompt ? (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {topupReason === 'manager_escrow'
                                    ? 'Manager escrow needs wallet balance. Top up USD funds (auto-reload helps on the next lock).'
                                    : 'Top up your brand wallet to continue.'}
                            </Alert>
                        ) : null}
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, mb: 1 }}>Add funds</Typography>
                        {fundOptions?.country_name && (
                            <Typography sx={{ fontSize: 12, color: t.form_label, mb: 2 }}>
                                {fundOptions.country_name}
                                {fundOptions.defaulted_to_usd
                                    ? ' · Card charged in USD'
                                    : ` · Card charged in ${
                                          fundOptions.payment_currency_upper ??
                                          fundOptions.payment_currency.toUpperCase()
                                      }`}
                            </Typography>
                        )}

                        {fundOptions?.exchange_rate?.description && !fundOptions.defaulted_to_usd && (
                            <Typography sx={{ fontSize: 12, color: t.form_label, mb: 2, lineHeight: 1.5 }}>
                                {fundOptions.exchange_rate.description}
                                {fundOptions.exchange_rate.as_of_iso
                                    ? ` · Rates as of ${new Date(fundOptions.exchange_rate.as_of_iso).toLocaleString()}`
                                    : ''}
                            </Typography>
                        )}

                        {fundOptions?.payment_rail?.fallback_note ? (
                            <Typography sx={{ fontSize: 12, color: t.form_label, mb: 1.5, lineHeight: 1.5 }}>
                                {fundOptions.payment_rail.fallback_note}
                            </Typography>
                        ) : null}

                        {paymentChannels ? (
                            <Typography sx={{ fontSize: 11, color: t.form_label, mb: 2 }}>
                                Paystack methods: {paymentChannels}
                            </Typography>
                        ) : null}

                        {/* Preset top-up buttons from /fund/options */}
                        {adminPresets.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                {fundOptions?.admin_preset_note && (
                                    <Typography sx={{ fontSize: 12, color: t.form_label, mb: 1.5, lineHeight: 1.5 }}>
                                        {fundOptions.admin_preset_note}
                                    </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {adminPresets.map(preset => {
                                        const active = selectedPresetId === preset.id;
                                        return (
                                            <Button
                                                key={preset.id}
                                                size="small"
                                                onClick={() => handlePresetSelect(preset)}
                                                sx={{
                                                    borderRadius: 2,
                                                    px: 2,
                                                    py: 1.25,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    fontSize: 12,
                                                    bgcolor: active ? String(BRAND_SECONDARY) + '22' : t.secondaryBg,
                                                    color: t.primary_white,
                                                    textTransform: 'none',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    border: active
                                                        ? '2px solid ' + BRAND_SECONDARY
                                                        : b(t.selectableBorder),
                                                    minWidth: { xs: '100%', sm: 200 },
                                                }}>
                                                <span>{getAdminTestPresetDisplayTitle(preset, fundOptions)}</span>
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: 11,
                                                        color: BRAND_SECONDARY,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        mt: 0.5,
                                                    }}>
                                                    Pay {formatYouPayDisplay(preset, fundOptions)}
                                                </Typography>
                                            </Button>
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}

                        {standardPresets.length > 0 && adminPresets.length > 0 && (
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                    color: t.form_label,
                                    mb: 1,
                                }}>
                                Standard top-ups
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            {loadingFundOptions && standardPresets.length === 0 && adminPresets.length === 0 && (
                                <CircularProgress size={20} sx={{ color: t.primary_orange }} />
                            )}
                            {standardPresets.map(preset => {
                                const active = selectedPresetId === preset.id;
                                return (
                                    <Button
                                        key={preset.id}
                                        size="small"
                                        onClick={() => handlePresetSelect(preset)}
                                        sx={{
                                            borderRadius: 2,
                                            px: 2,
                                            py: 1.25,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 12,
                                            bgcolor: active ? String(t.primary_orange) + '18' : t.secondaryBg,
                                            color: t.primary_white,
                                            textTransform: 'none',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            border: active ? '2px solid ' + t.primary_orange : b(t.selectableBorder),
                                            minWidth: { xs: '100%', sm: 160 },
                                        }}>
                                        <span>
                                            {formatApiAmountLabel(
                                                preset.labels?.wallet_credit_primary ?? preset.wallet_credit.formatted,
                                                fundOptions,
                                                preset,
                                            ) ??
                                                preset.labels?.wallet_credit_primary ??
                                                preset.wallet_credit.formatted}
                                        </span>
                                        {!fundOptions?.defaulted_to_usd && preset.labels?.wallet_credit_secondary && (
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontSize: 10,
                                                    color: t.form_label,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    mt: 0.25,
                                                }}>
                                                {preset.labels.wallet_credit_secondary}
                                            </Typography>
                                        )}
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontSize: 11,
                                                color: BRAND_SECONDARY,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                mt: 0.5,
                                            }}>
                                            Pay {formatYouPayDisplay(preset, fundOptions)}
                                        </Typography>
                                    </Button>
                                );
                            })}
                        </Box>

                        {/* Custom USD wallet credit */}
                        <Typography
                            sx={{
                                fontSize: 11,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: t.form_label,
                                mb: 1,
                            }}>
                            {chargesInLocal
                                ? 'Or enter custom amount (card charge)'
                                : 'Or enter custom amount (USD wallet credit)'}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="0"
                            type="text"
                            inputMode="decimal"
                            value={amountStr}
                            onChange={e => handleAmountChange(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 24,
                                                color: t.primary_orange,
                                            }}>
                                            {paySymbol}
                                        </Typography>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: t.prisma_dark,
                                    borderRadius: 2,
                                    py: 0.5,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 28,
                                    color: t.primary_white,
                                    '& fieldset': { border: 'none' },
                                },
                                '& .MuiInputBase-input': { color: t.primary_white },
                                '& .MuiInputBase-input::placeholder': { color: t.form_label, opacity: 1 },
                            }}
                        />

                        {fundOptions?.minimum_fund && (
                            <Typography sx={{ fontSize: 12, color: t.form_label, mt: 1.5 }}>
                                {getMinimumFundLabel(fundOptions)}
                            </Typography>
                        )}
                        <Typography sx={{ fontSize: 11, color: t.form_label, mt: 2, fontStyle: 'italic' }}>
                            Wallet balance is held in US dollars (USD).
                        </Typography>

                        {/* Payment method selector */}
                        <Typography
                            sx={{
                                fontSize: 11,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: t.form_label,
                                mt: 3,
                                mb: 1.5,
                            }}>
                            Select payment method
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                            {PAYMENT_METHODS.map((opt, i) => {
                                const IconComp = opt.Icon;
                                const active = method === i && !opt.locked;
                                return (
                                    <Box
                                        key={opt.label}
                                        onClick={() => !opt.locked && setMethod(i)}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            cursor: opt.locked ? 'default' : 'pointer',
                                            border: active ? '2px solid ' + t.primary_orange : b(t.selectableBorder),
                                            bgcolor: active ? String(t.primary_orange) + '12' : t.secondaryBg,
                                            opacity: opt.locked ? 0.5 : 1,
                                            transition: 'border-color 0.2s',
                                            position: 'relative',
                                        }}>
                                        {opt.locked && (
                                            <LockOutlinedIcon
                                                sx={{
                                                    position: 'absolute',
                                                    top: 10,
                                                    right: 10,
                                                    fontSize: 16,
                                                    color: t.form_label,
                                                }}
                                            />
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <IconComp
                                                sx={{ fontSize: 32, color: active ? t.primary_orange : t.form_label }}
                                            />
                                        </Box>
                                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14 }}>
                                            {opt.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: t.form_label, mt: 0.5 }}>
                                            {opt.sub}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Auto-reload toggle */}
                        <Box
                            sx={{
                                mt: 3,
                                p: 2,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap',
                                bgcolor: t.secondaryBg,
                                border: b(t.selectableBorder),
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        bgcolor: BRAND_SECONDARY + '22',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: BRAND_SECONDARY,
                                    }}>
                                    <PublishedWithChangesIcon />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14 }}>
                                        Auto-reload balance
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: t.form_label }}>
                                        Refill when balance drops below $1,000
                                    </Typography>
                                </Box>
                            </Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={autoReload}
                                        onChange={handleAutoReloadToggle}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_SECONDARY },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                bgcolor: BRAND_SECONDARY + '99',
                                            },
                                        }}
                                    />
                                }
                                label=""
                            />
                        </Box>

                        {method === 0 && showHostedCheckout ? (
                            <WalletPaystackFunding
                                walletCreditUsdCents={walletCreditUsdCents}
                                fundOptions={fundOptions}
                                selectedPreset={selectedPreset}
                                activePreset={activePreset}
                                customPaymentChargeCents={customPaymentChargeCents}
                                onSwitchToStripe={() => setFundingProvider('stripe')}
                                stripeAvailable={stripeCheckoutAvailable(fundOptions)}
                                provider={showFlutterwave ? 'flutterwave' : 'paystack'}
                            />
                        ) : null}

                        {method === 0 && showStripe ? (
                            <>
                                {paystackCheckoutAvailable(fundOptions) ? (
                                    <Typography sx={{ fontSize: 12, color: t.form_label, mb: 1 }}>
                                        <Button
                                            size="small"
                                            onClick={() => setFundingProvider('paystack')}
                                            sx={{
                                                textTransform: 'none',
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: t.primary_orange,
                                                p: 0,
                                                minWidth: 0,
                                            }}>
                                            Use Paystack (bank transfer / local methods)
                                        </Button>
                                    </Typography>
                                ) : flutterwaveCheckoutAvailable(fundOptions) ? (
                                    <Typography sx={{ fontSize: 12, color: t.form_label, mb: 1 }}>
                                        <Button
                                            size="small"
                                            onClick={() => setFundingProvider('flutterwave')}
                                            sx={{
                                                textTransform: 'none',
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: t.primary_orange,
                                                p: 0,
                                                minWidth: 0,
                                            }}>
                                            Use Flutterwave (local methods)
                                        </Button>
                                    </Typography>
                                ) : null}
                                <WalletPaymentSection
                                    amountCents={walletCreditUsdCents}
                                    customPaymentChargeCents={customPaymentChargeCents}
                                    fundOptions={fundOptions}
                                    selectedPreset={selectedPreset}
                                    activePreset={activePreset}
                                    onPaymentComplete={handlePaymentComplete}
                                />
                            </>
                        ) : null}
                    </Box>
                </Box>

                {/* ── Right column ────────────────────────────── */}
                <Box
                    sx={{
                        borderRadius: 2,
                        p: 3,
                        bgcolor: t.deep_grey_lighter,
                        border: b(t.selectableBorder),
                        height: '100%',
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18 }}>
                            Transaction history
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: t.primary_orange,
                                cursor: 'pointer',
                            }}>
                            View all
                        </Typography>
                    </Box>

                    {transactions.length === 0 && (
                        <Typography sx={{ fontSize: 13, color: t.form_label, py: 4, textAlign: 'center' }}>
                            No transactions yet
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {transactions.map(tx => {
                            const positive = tx.amount_cents > 0;
                            return (
                                <Box
                                    key={tx._id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        alignItems: 'center',
                                    }}>
                                    <Box>
                                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14 }}>
                                            {txLabel(tx)}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: t.form_label }}>
                                            {txDate(tx.createdAt)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14 }}>
                                            {positive ? '+' : ''}${formatCents(Math.abs(tx.amount_cents))}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 10,
                                                mt: 0.5,
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: 999,
                                                display: 'inline-block',
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                textTransform: 'uppercase',
                                                bgcolor:
                                                    tx.status === 'completed' ? BRAND_SECONDARY + '22' : t.prisma_dark,
                                                color: tx.status === 'completed' ? BRAND_SECONDARY : t.form_label,
                                            }}>
                                            {tx.status}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Funding activity chart */}
                    <Box
                        sx={{
                            mt: 4,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: String(t.prisma_dark) + '88',
                            border: b(t.selectableBorder),
                        }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: t.form_label,
                                mb: 2,
                            }}>
                            Funding activity (12m)
                        </Typography>
                        <Box sx={{ height: 120, display: 'flex', alignItems: 'flex-end', gap: 0.75, px: 1 }}>
                            {(fundingActivity.length > 0 ? fundingActivity : Array(12).fill(null)).map((entry, i) => {
                                const pct = entry ? Math.max((entry.total_cents / maxBar) * 100, 4) : 4;
                                const isHighest = entry && entry.total_cents === maxBar && maxBar > 0;
                                return (
                                    <Box
                                        key={entry?.month ?? i}
                                        title={entry ? `${entry.label}: $${formatCents(entry.total_cents)}` : ''}
                                        sx={{
                                            flex: 1,
                                            height: pct + '%',
                                            borderRadius: '4px 4px 0 0',
                                            bgcolor: isHighest ? BRAND_SECONDARY + '44' : t.secondaryBg,
                                            borderTop: isHighest ? '2px solid ' + BRAND_SECONDARY : 'none',
                                            transition: 'height 0.3s',
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </MetaTags>
    );
}

/* ── Balance card (extracted to keep main component concise) ───────────── */

function BalanceCard({
    t,
    b,
    availableFunds,
    pctChange,
    escrow,
    spentThisMonth,
}: {
    t: any;
    b: (c: string) => string;
    availableFunds: number;
    pctChange: number;
    escrow: number;
    spentThisMonth: number;
}) {
    const positive = pctChange >= 0;
    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 2,
                p: 3,
                bgcolor: t.secondaryBg,
                border: b(t.selectableBorder),
                boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    top: -64,
                    right: -64,
                    width: 220,
                    height: 220,
                    borderRadius: '50%',
                    bgcolor: String(t.primary_orange) + '14',
                    filter: 'blur(40px)',
                }}
            />
            <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 11,
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                        color: t.form_label,
                        mb: 1,
                    }}>
                    Available campaign funds
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: { xs: 36, sm: 44 }, color: t.primary_white }}>
                        ${formatCents(availableFunds)}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: positive ? BRAND_SECONDARY : '#ff7351',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}>
                        {positive ? (
                            <TrendingUpIcon sx={{ fontSize: 16 }} />
                        ) : (
                            <TrendingDownIcon sx={{ fontSize: 16 }} />
                        )}
                        {positive ? '+' : ''}
                        {pctChange.toFixed(1)}%
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                    {[
                        { k: 'Locked in escrow', v: '$' + formatCents(escrow) },
                        { k: 'Spent this month', v: '$' + formatCents(spentThisMonth) },
                    ].map(row => (
                        <Box
                            key={row.k}
                            sx={{
                                flex: 1,
                                p: 2,
                                borderRadius: 2,
                                bgcolor: t.deep_grey_lighter,
                                border: b(t.selectableBorder),
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    letterSpacing: '0.15em',
                                    textTransform: 'uppercase',
                                    color: t.form_label,
                                }}>
                                {row.k}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, mt: 0.5 }}>
                                {row.v}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
